import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Typography,
  Autocomplete,
  useTheme,
  IconButton,
  Snackbar,
  Alert,
  Paper,
  Divider,
  Stack,
  CircularProgress,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CurrencyRupeeOutlinedIcon from "@mui/icons-material/CurrencyRupeeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ApiConfig from "../../Config";

const AddSingleProduct = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isDark = theme.palette.mode === "dark";

  const [productName, setProductName] = useState("");
  const [productDescription, setProductDescription] = useState("");
  const [productShortDescription, setProductShortDescription] = useState("");
  const [mainCategoryId, setMainCategoryId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const [actualPrice, setActualPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [sPrice, setSPrice] = useState("");
  const [stockCount, setStockCount] = useState("");
  const [images, setImages] = useState([]);
  const [mainCategories, setMainCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [subcategoryLoading, setSubcategoryLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const colors = {
    background: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#191715" : "#FFFFFF",
    input: isDark ? "#151412" : "#FCFBF9",
    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#AAA49C" : "#77716A",
    accent: isDark ? "#D1B99A" : "#806B52",
    accentSoft: isDark ? "rgba(209,185,154,.10)" : "rgba(128,107,82,.08)",
    border: isDark ? "rgba(255,255,255,.08)" : "rgba(50,40,30,.10)",
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: colors.input,
      color: colors.text,
      "& fieldset": { borderColor: colors.border },
      "&:hover fieldset": { borderColor: colors.accent },
      "&.Mui-focused fieldset": {
        borderColor: colors.accent,
        borderWidth: "1px",
      },
    },
    "& .MuiInputLabel-root": { color: colors.secondary },
    "& .MuiInputLabel-root.Mui-focused": { color: colors.accent },
    "& .MuiInputBase-input::placeholder": {
      color: colors.secondary,
      opacity: 0.7,
    },
  };

  const sectionSx = {
    p: { xs: 2, sm: 3, md: 3.5 },
    borderRadius: "14px",
    backgroundColor: colors.card,
    border: `1px solid ${colors.border}`,
    boxShadow: isDark
      ? "0 8px 30px rgba(0,0,0,.12)"
      : "0 8px 30px rgba(50,40,30,.045)",
  };

  const notify = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const response = await axios.get(
          `${ApiConfig.apiBaseUrl}/admin/getMainCat`,
        );

        if (mounted) setMainCategories(response.data?.MainCategory || []);
      } catch (error) {
        console.error("Error fetching main categories:", error);
        notify("Unable to load main categories", "error");
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  const handleCategoryChange = async (_, newValue) => {
    setMainCategoryId(newValue?.mainCategoryId || "");
    setCategoryId("");
    setSubCategoryId("");
    setCategories([]);
    setSubcategories([]);

    if (!newValue?.mainCategoryId) return;

    try {
      setCategoryLoading(true);
      const response = await axios.get(`${ApiConfig.base}/admin/allCategory`, {
        headers: { "main-id": newValue.mainCategoryId },
      });
      setCategories(response.data?.category || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
      notify("Unable to load categories", "error");
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleSubCategoryChange = async (_, newValue) => {
    setCategoryId(newValue?.categoryId || "");
    setSubCategoryId("");
    setSubcategories([]);

    if (!newValue?.categoryId) return;

    try {
      setSubcategoryLoading(true);
      const response = await axios.get(
        `${ApiConfig.apiBaseUrl}/admin/allSubcategory`,
        {
          headers: { "category-id": newValue.categoryId },
        },
      );
      setSubcategories(response.data?.Subcategory || []);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      notify("Unable to load subcategories", "error");
    } finally {
      setSubcategoryLoading(false);
    }
  };

  const handleSubcategorySelect = (_, newValue) => {
    setSubCategoryId(newValue?.subCategoryId || "");
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    const existingKeys = new Set(
      images.map((file) => `${file.name}-${file.size}`),
    );
    const validFiles = [];
    let invalidCount = 0;
    let duplicateCount = 0;

    files.forEach((file) => {
      if (file.size > 5 * 1024 * 1024 || !validTypes.includes(file.type)) {
        invalidCount++;
        return;
      }
      const key = `${file.name}-${file.size}`;
      if (existingKeys.has(key)) {
        duplicateCount++;
        return;
      }
      existingKeys.add(key);
      validFiles.push(file);
    });

    setImages((prev) => [...prev, ...validFiles]);

    if (invalidCount > 0) {
      notify(
        `${invalidCount} image(s) were rejected. Maximum size is 5MB.`,
        "warning",
      );
    } else if (duplicateCount > 0) {
      notify("Duplicate image(s) were skipped.", "warning");
    }

    e.target.value = "";
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    const actual = Number(actualPrice);
    const max = Number(maxPrice);
    const sale = Number(sPrice);
    const stock = Number(stockCount);

    if (!productName.trim()) {
      notify("Product name is required", "error");
      return false;
    }

    if (!mainCategoryId) {
      notify("Please select a main category", "error");
      return false;
    }

    if (!categoryId) {
      notify("Please select a category", "error");
      return false;
    }

    if (!subCategoryId) {
      notify("Please select a subcategory", "error");
      return false;
    }

    if (!actualPrice || actual <= 0) {
      notify("Please enter a valid actual price", "error");
      return false;
    }

    if (!maxPrice || max <= 0) {
      notify("Please enter a valid maximum price", "error");
      return false;
    }

    if (!sPrice || sale <= 0) {
      notify("Please enter a valid sales price", "error");
      return false;
    }

    if (max <= actual) {
      notify("Max Price must be greater than Actual Price.", "error");
      return false;
    }

    if (sale >= actual) {
      notify("Sales Price must be less than Actual Price.", "error");
      return false;
    }

    if (stockCount === "" || stock < 0) {
      notify("Please enter a valid stock count", "error");
      return false;
    }

    if (images.length === 0) {
      notify("Please upload at least one product image", "error");
      return false;
    }

    return true;
  };

  const resetForm = () => {
    setProductName("");
    setProductDescription("");
    setProductShortDescription("");
    setMainCategoryId("");
    setCategoryId("");
    setSubCategoryId("");
    setActualPrice("");
    setMaxPrice("");
    setSPrice("");
    setStockCount("");
    setImages([]);
    setCategories([]);
    setSubcategories([]);
  };

  const handleSubmit = async () => {
    if (loading) return;
    if (!validateForm()) return;

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("productName", productName.trim());
      formData.append(
        "product_ShortDescription",
        productShortDescription.trim(),
      );
      formData.append("product_Description", productDescription.trim());
      formData.append("mainCategoryId", mainCategoryId);
      formData.append("categoryId", categoryId);
      formData.append("subCategoryId", subCategoryId);
      formData.append("actual_price", actualPrice);
      formData.append("max_price", maxPrice);
      formData.append("s_Price", sPrice);
      formData.append("stockCount", stockCount);

      images.forEach((image) => {
        formData.append("imageURL", image);
      });

      const response = await axios.post(`${ApiConfig.apiBaseUrl}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200 || response.status === 201) {
        notify("Product added successfully", "success");
        resetForm();
      }
    } catch (error) {
      console.error("Error adding product:", error);
      notify(
        error?.response?.data?.message || "Failed to add product",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const ImagePreview = ({ image, index }) => (
    <Box
      sx={{
        position: "relative",
        width: { xs: 88, sm: 100 },
        height: { xs: 110, sm: 125 },
        borderRadius: "10px",
        overflow: "hidden",
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.input,
        flexShrink: 0,
      }}
    >
      <img
        src={URL.createObjectURL(image)}
        alt={image.name}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
      <IconButton
        onClick={() => handleRemoveImage(index)}
        size="small"
        sx={{
          position: "absolute",
          top: 5,
          right: 5,
          width: 27,
          height: 27,
          backgroundColor: "rgba(0,0,0,.62)",
          color: "#fff",
          "&:hover": { backgroundColor: "rgba(0,0,0,.8)" },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100%",
        backgroundColor: colors.background,
        color: colors.text,
        p: { xs: 1.5, sm: 2.5, md: 4 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          maxWidth: 1400,
          mx: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            mb: { xs: 2.5, md: 3.5 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                mr: 1.5,
                width: 42,
                height: 42,
                border: `1px solid ${colors.border}`,
                borderRadius: "10px",
                color: colors.text,
                backgroundColor: colors.card,
                "&:hover": { backgroundColor: colors.accentSoft },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
            </IconButton>

            <Box>
              <Typography
                sx={{
                  fontFamily: "Georgia, 'Times New Roman', serif",
                  fontSize: { xs: 22, sm: 27, md: 30 },
                  fontWeight: 600,
                  lineHeight: 1.1,
                  color: colors.text,
                }}
              >
                Add Product
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: 11.5, sm: 13 },
                  color: colors.secondary,
                  mt: 0.5,
                }}
              >
                Create a new product for your wardrobe catalog
              </Typography>
            </Box>
          </Box>

          <Chip
            icon={<Inventory2OutlinedIcon />}
            label="Product Catalog"
            sx={{
              display: { xs: "none", sm: "flex" },
              height: 36,
              borderRadius: "9px",
              color: colors.accent,
              backgroundColor: colors.accentSoft,
              border: `1px solid ${colors.border}`,
              "& .MuiChip-icon": { color: colors.accent },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1.15fr .85fr" },
            gap: { xs: 2, md: 2.5 },
            alignItems: "start",
          }}
        >
          <Stack spacing={2.5}>
            <Paper elevation={0} sx={sectionSx}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "9px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: colors.accentSoft,
                    color: colors.accent,
                  }}
                >
                  <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 650, color: colors.text }}
                  >
                    Product Information
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: colors.secondary }}>
                    Basic details and product description
                  </Typography>
                </Box>
              </Box>

              <Stack spacing={2}>
                <TextField
                  label="Product Name"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  fullWidth
                  required
                  sx={fieldSx}
                />

                <TextField
                  label="Short Description"
                  value={productShortDescription}
                  onChange={(e) => setProductShortDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  sx={fieldSx}
                />

                <TextField
                  label="Product Description"
                  value={productDescription}
                  onChange={(e) => setProductDescription(e.target.value)}
                  fullWidth
                  multiline
                  minRows={5}
                  sx={fieldSx}
                />
              </Stack>
            </Paper>

            <Paper elevation={0} sx={sectionSx}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "9px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: colors.accentSoft,
                    color: colors.accent,
                  }}
                >
                  <CategoryOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 650, color: colors.text }}
                  >
                    Category
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: colors.secondary }}>
                    Choose where this product belongs
                  </Typography>
                </Box>
              </Box>

              <Stack spacing={2}>
                <Autocomplete
                  options={mainCategories}
                  getOptionLabel={(option) => option?.mainCategoryName || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.mainCategoryId === value?.mainCategoryId
                  }
                  onChange={handleCategoryChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Main Category"
                      required
                      sx={fieldSx}
                    />
                  )}
                />

                <Autocomplete
                  options={categories}
                  disabled={!mainCategoryId}
                  loading={categoryLoading}
                  getOptionLabel={(option) => option?.categoryName || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.categoryId === value?.categoryId
                  }
                  onChange={handleSubCategoryChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      {...params}
                      label="Category"
                      required
                      sx={fieldSx}
                    />
                  )}
                />

                <Autocomplete
                  options={subcategories}
                  disabled={!categoryId}
                  loading={subcategoryLoading}
                  getOptionLabel={(option) => option?.subCategoryName || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.subCategoryId === value?.subCategoryId
                  }
                  onChange={handleSubcategorySelect}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Subcategory"
                      required
                      sx={fieldSx}
                    />
                  )}
                />
              </Stack>
            </Paper>

            <Paper elevation={0} sx={sectionSx}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "9px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: colors.accentSoft,
                    color: colors.accent,
                  }}
                >
                  <CurrencyRupeeOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 650, color: colors.text }}
                  >
                    Pricing & Stock
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: colors.secondary }}>
                    Set product pricing and available inventory
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2,1fr)",
                    md: "repeat(4,1fr)",
                  },
                  gap: 2,
                }}
              >
                <TextField
                  label="Actual Price"
                  type="number"
                  value={actualPrice}
                  onChange={(e) => setActualPrice(e.target.value)}
                  required
                  sx={fieldSx}
                />

                <TextField
                  label="Max Price"
                  type="number"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  required
                  sx={fieldSx}
                />

                <TextField
                  label="Sales Price"
                  type="number"
                  value={sPrice}
                  onChange={(e) => setSPrice(e.target.value)}
                  required
                  sx={fieldSx}
                />

                <TextField
                  label="Stock Count"
                  type="number"
                  value={stockCount}
                  onChange={(e) => setStockCount(e.target.value)}
                  required
                  sx={fieldSx}
                />
              </Box>

              {actualPrice &&
                sPrice &&
                Number(sPrice) < Number(actualPrice) && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      color: colors.accent,
                    }}
                  >
                    <CheckCircleOutlineRoundedIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontSize: 12.5 }}>
                      Sale price is below actual price.
                    </Typography>
                  </Box>
                )}
            </Paper>
          </Stack>

          <Stack spacing={2.5}>
            <Paper elevation={0} sx={sectionSx}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.2,
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: "9px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: colors.accentSoft,
                    color: colors.accent,
                  }}
                >
                  <AddPhotoAlternateOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography
                    sx={{ fontSize: 15, fontWeight: 650, color: colors.text }}
                  >
                    Product Images
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: colors.secondary }}>
                    Upload clear product photos
                  </Typography>
                </Box>
              </Box>

              <input
                accept="image/jpeg,image/png,image/gif,image/webp"
                id="product-image-upload"
                type="file"
                multiple
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />

              <label htmlFor="product-image-upload">
                <Box
                  sx={{
                    border: `1.5px dashed ${colors.border}`,
                    borderRadius: "12px",
                    p: { xs: 2.5, sm: 3 },
                    textAlign: "center",
                    cursor: "pointer",
                    backgroundColor: colors.input,
                    transition: "all .2s ease",
                    "&:hover": {
                      borderColor: colors.accent,
                      backgroundColor: colors.accentSoft,
                    },
                  }}
                >
                  <PhotoCameraOutlinedIcon
                    sx={{
                      fontSize: 32,
                      color: colors.accent,
                      mb: 1,
                    }}
                  />
                  <Typography
                    sx={{ fontSize: 13.5, fontWeight: 600, color: colors.text }}
                  >
                    Click to upload images
                  </Typography>
                  <Typography
                    sx={{ fontSize: 11, color: colors.secondary, mt: 0.5 }}
                  >
                    JPG, PNG, GIF or WEBP • Maximum 5MB each
                  </Typography>
                </Box>
              </label>

              {images.length > 0 && (
                <>
                  <Divider sx={{ my: 2, borderColor: colors.border }} />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        color: colors.text,
                      }}
                    >
                      Selected Images
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: colors.secondary }}>
                      {images.length} image{images.length > 1 ? "s" : ""}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1.2,
                      flexWrap: "wrap",
                    }}
                  >
                    {images.map((image, index) => (
                      <ImagePreview
                        key={`${image.name}-${image.size}-${index}`}
                        image={image}
                        index={index}
                      />
                    ))}
                  </Box>
                </>
              )}
            </Paper>

            <Paper elevation={0} sx={sectionSx}>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 650,
                  color: colors.text,
                  mb: 0.8,
                }}
              >
                Ready to publish?
              </Typography>
              <Typography
                sx={{ fontSize: 12, color: colors.secondary, lineHeight: 1.6 }}
              >
                Review the product details, pricing, category and images before
                adding it to the catalog.
              </Typography>

              <Divider sx={{ my: 2, borderColor: colors.border }} />

              <Button
                fullWidth
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={17} sx={{ color: "inherit" }} />
                  ) : (
                    <CheckCircleOutlineRoundedIcon />
                  )
                }
                sx={{
                  height: 48,
                  borderRadius: "10px",
                  backgroundColor: colors.accent,
                  color: "#fff",
                  fontSize: 13.5,
                  fontWeight: 650,
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: isDark ? "#BFA689" : "#6E5B47",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: colors.accent,
                    color: "#fff",
                    opacity: 0.65,
                  },
                }}
              >
                {loading ? "Adding Product..." : "Add Product"}
              </Button>
            </Paper>
          </Stack>
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ borderRadius: "10px" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddSingleProduct;
